// src/components/common/ImageModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Eye } from 'lucide-react';

export const ImageModal = ({ 
  isOpen, 
  onClose, 
  images = [], 
  initialIndex = 0, 
  title = '', 
  description = '',
  allowDownload = false,
  customStyling = {}
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextImage();
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, currentIndex]);

  // Reset zoom when image changes
  useEffect(() => {
    setZoomLevel(1);
    setIsZoomed(false);
  }, [currentIndex]);

  // Touch event handlers for swipe navigation
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && images.length > 1) {
      nextImage();
    }
    if (isRightSwipe && images.length > 1) {
      previousImage();
    }
  };

  if (!isOpen || !images.length) return null;

  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const previousImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
    setIsZoomed(true);
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
    if (zoomLevel <= 1) setIsZoomed(false);
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setIsZoomed(false);
  };

  const downloadImage = async () => {
    try {
      const response = await fetch(currentImage.url || currentImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentImage.name || `image-${currentIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
      ref={containerRef}
    >
      {/* Header - Responsive padding */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black via-black/50 to-transparent p-2 sm:p-4 z-10">
        <div className="flex items-center justify-between text-white">
          <div className="flex-1 min-w-0">
            {title && (
              <h2 className="text-lg sm:text-xl font-semibold truncate">
                {title}
              </h2>
            )}
            {hasMultipleImages && (
              <p className="text-xs sm:text-sm text-gray-300">
                {currentIndex + 1} of {images.length}
              </p>
            )}
          </div>
          
          {/* Action Buttons - Hide some on mobile */}
          <div className="flex items-center gap-1 sm:gap-2 ml-2">
            {/* Zoom controls - hidden on mobile */}
            <div className="hidden sm:flex items-center gap-1">
              <button
                onClick={zoomOut}
                disabled={zoomLevel <= 0.5}
                className="p-2 rounded-lg bg-black bg-opacity-50 hover:bg-opacity-70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              
              <button
                onClick={zoomIn}
                disabled={zoomLevel >= 3}
                className="p-2 rounded-lg bg-black bg-opacity-50 hover:bg-opacity-70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            {/* Download - hidden on mobile if space is limited */}
            {allowDownload && (
              <button
                onClick={downloadImage}
                className="hidden sm:block p-2 rounded-lg bg-black bg-opacity-50 hover:bg-opacity-70 transition-all"
                title="Download Image"
              >
                <Download className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-black bg-opacity-50 hover:bg-opacity-70 transition-all"
              title="Close (Esc)"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Hidden on mobile/tablet */}
      {hasMultipleImages && (
        <>
          <button
            onClick={previousImage}
            className="hidden lg:block absolute left-2 xl:left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 text-white transition-all z-10"
            title="Previous Image (←)"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            onClick={nextImage}
            className="hidden lg:block absolute right-2 xl:right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 text-white transition-all z-10"
            title="Next Image (→)"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Main Image Container - Full width on mobile */}
      <div 
        className="flex items-center justify-center w-full h-full p-2 sm:p-8 lg:p-16 cursor-pointer"
        onClick={isZoomed ? resetZoom : onClose}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          ref={imageRef}
          src={currentImage.url || currentImage}
          alt={currentImage.alt || currentImage.caption || `Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain transition-transform duration-300 ease-in-out"
          style={{ 
            transform: `scale(${zoomLevel})`,
            cursor: isZoomed ? 'zoom-out' : 'zoom-in'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (window.innerWidth >= 640) { // Only allow zoom on larger screens
              if (isZoomed) {
                resetZoom();
              } else {
                zoomIn();
              }
            }
          }}
          onError={(e) => {
            console.error('Failed to load image:', currentImage);
            e.target.src = '/placeholder-image.jpg';
          }}
        />
      </div>

      {/* Swipe Indicator - Show on mobile when multiple images */}
      {hasMultipleImages && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 sm:hidden">
          <div className="bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-xs">
            Swipe to navigate
          </div>
        </div>
      )}

      {/* Footer - Caption */}
      {(description || currentImage.caption) && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent p-2 sm:p-4">
          <p className="text-white text-center max-w-2xl mx-auto text-sm sm:text-base">
            {currentImage.caption || description}
          </p>
        </div>
      )}

      {/* Thumbnail Strip - Hidden on very small screens, horizontal scroll on mobile */}
      {hasMultipleImages && images.length > 1 && (
        <div className="hidden sm:block absolute bottom-16 lg:bottom-20 left-1/2 transform -translate-x-1/2">
          <div className="flex gap-1 sm:gap-2 bg-black bg-opacity-50 rounded-lg p-1 sm:p-2 max-w-xs sm:max-w-md overflow-x-auto scrollbar-hide">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 w-8 h-8 sm:w-12 sm:h-12 rounded overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-white shadow-lg' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={image.url || image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mobile Navigation Dots */}
      {hasMultipleImages && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 sm:hidden">
          <div className="flex gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-white'
                    : 'bg-white bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};